import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { StoreCore } from '../types/store';
import { TimeEntry, TimerState, TimerTarget } from '../types/activity';
import { DEMO_DATE } from '../data/demo';
import { timerElapsedMs } from '../utils/time';
import { withOcc } from '../utils/db';
import { isDoneStatus } from '../utils/progress';

export interface SaveSessionInput {
  minutes: number;
  templateId?: string;
  freeLabel?: string;
  note?: string;
  alsoCheck: boolean;
}

export interface ManualEntryInput {
  minutes: number;
  date: string;
  templateId?: string;
  freeLabel?: string;
}

export function useTimerActions(core: StoreCore) {
  const { db, setDb, family, viewer } = core;
  const timerKey = `${family.id}:${viewer.id}`;
  const timer = db.timers[timerKey] ?? null;

  const activityLabel = useCallback(
    (target: TimerTarget) =>
      family.templates.find((t) => t.id === target.templateId)?.title ?? (target.freeLabel?.trim() || 'Prosta dejavnost'),
    [family]
  );

  const buildEntry = useCallback(
    (t: TimerState, minutes: number, target: TimerTarget, note?: string): TimeEntry => ({
      id: t.sessionId,
      familyId: family.id,
      memberId: viewer.id,
      date: DEMO_DATE,
      activity: activityLabel(target),
      minutes,
      templateId: target.templateId,
      source: 'casovnik',
      sync: 'pending',
      note,
    }),
    [family.id, viewer.id, activityLabel]
  );

  const startTimer = useCallback(
    (target: TimerTarget, force = false): 'started' | 'conflict' => {
      const existing = db.timers[timerKey];
      if (existing && !force) return 'conflict';
      const now = Date.now();
      setDb((prev) => {
        const entries = [...prev.entries];
        if (existing) {
          const minutes = Math.round(timerElapsedMs(existing, now) / 60000);
          if (minutes > 0 && !entries.some((e) => e.id === existing.sessionId)) entries.unshift(buildEntry(existing, minutes, existing.target));
        }
        const next: TimerState = {
          sessionId: `s-${now}`,
          familyId: family.id,
          memberId: viewer.id,
          target,
          status: 'running',
          segmentStart: now,
          accumulatedMs: 0,
        };
        return { ...prev, entries, timers: { ...prev.timers, [timerKey]: next } };
      });
      if (existing) toast('Prejšnja seja je shranjena v dnevnik časa');
      return 'started';
    },
    [db.timers, timerKey, setDb, buildEntry, family.id, viewer.id]
  );

  const updateTimer = useCallback(
    (fn: (t: TimerState, now: number) => TimerState) =>
      setDb((prev) => {
        const t = prev.timers[timerKey];
        return t ? { ...prev, timers: { ...prev.timers, [timerKey]: fn(t, Date.now()) } } : prev;
      }),
    [setDb, timerKey]
  );

  const pauseTimer = useCallback(
    () => updateTimer((t, now) => (t.status !== 'running' ? t : { ...t, status: 'paused', accumulatedMs: timerElapsedMs(t, now), segmentStart: null })),
    [updateTimer]
  );
  const resumeTimer = useCallback(
    () => updateTimer((t, now) => (t.status !== 'paused' ? t : { ...t, status: 'running', segmentStart: now })),
    [updateTimer]
  );
  const finishTimer = useCallback(
    () =>
      updateTimer((t, now) => {
        if (t.status === 'review') return t;
        const ms = timerElapsedMs(t, now);
        return { ...t, status: 'review', finalMs: ms, accumulatedMs: ms, segmentStart: null };
      }),
    [updateTimer]
  );
  const discardTimer = useCallback(
    () =>
      setDb((prev) => {
        const timers = { ...prev.timers };
        delete timers[timerKey];
        return { ...prev, timers };
      }),
    [setDb, timerKey]
  );

  const saveSession = useCallback(
    (input: SaveSessionInput) => {
      const current = db.timers[timerKey];
      if (!current) return;
      const target: TimerTarget = { templateId: input.templateId, freeLabel: input.freeLabel };
      setDb((prev) => {
        const timers = { ...prev.timers };
        delete timers[timerKey];
        // Ista seja se nikoli ne shrani dvakrat.
        if (prev.entries.some((e) => e.id === current.sessionId)) return { ...prev, timers };
        let next: typeof prev = { ...prev, timers };
        const t = family.templates.find((x) => x.id === input.templateId);
        const cur = prev.occ[family.id]?.[input.templateId ?? ''];
        if (input.alsoCheck && t && t.confirmation === 'zaupanje' && t.assigneeIds.includes(viewer.id) && !isDoneStatus(cur?.status)) {
          next = withOcc(next, family.id, t.id, { status: 'opravljeno', sync: 'pending', performerId: viewer.id });
        }
        if (input.minutes > 0) next = { ...next, entries: [buildEntry(current, input.minutes, target, input.note), ...next.entries] };
        return next;
      });
      toast.success(input.alsoCheck ? `Shranjeno ${input.minutes} min in obkljukano` : `Shranjeno ${input.minutes} min v dnevnik časa`);
    },
    [db.timers, timerKey, setDb, family, viewer.id, buildEntry]
  );

  const entries = useMemo(() => db.entries.filter((e) => e.familyId === family.id), [db.entries, family.id]);

  const addManualEntry = useCallback(
    (input: ManualEntryInput) => {
      const entry: TimeEntry = {
        id: `m-${Date.now()}`,
        familyId: family.id,
        memberId: viewer.id,
        date: input.date,
        activity: activityLabel({ templateId: input.templateId, freeLabel: input.freeLabel }),
        minutes: input.minutes,
        templateId: input.templateId,
        source: 'rocno',
        sync: 'pending',
      };
      setDb((prev) => ({ ...prev, entries: [entry, ...prev.entries] }));
      toast.success(`Vpisano ${input.minutes} min`);
    },
    [family.id, viewer.id, activityLabel, setDb]
  );

  const minutesToday = useCallback(
    (templateId: string, memberId?: string) =>
      entries
        .filter((e) => e.templateId === templateId && e.memberId === (memberId ?? viewer.id) && e.date === DEMO_DATE)
        .reduce((sum, e) => sum + e.minutes, 0),
    [entries, viewer.id]
  );

  return { timer, activityLabel, startTimer, pauseTimer, resumeTimer, finishTimer, discardTimer, saveSession, entries, addManualEntry, minutesToday };
}
